import { createFileRoute, Link } from "@tanstack/react-router";
import heroAsset from "@/assets/hero-v2.jpg.asset.json";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { button3DClass } from "@/components/button-3d";

export const Route = createFileRoute("/careers")({
  head: () => ({
    meta: [
      { title: "Careers | MG Content Labs" },
      {
        name: "description",
        content:
          "Learn about the MG Content Labs team. We keep a rolling talent pool of writers, SEO strategists, and social media specialists who work with B2B and SaaS brands.",
      },
      { property: "og:title", content: "Careers | MG Content Labs" },
      {
        property: "og:description",
        content:
          "We keep a rolling talent pool of writers, SEO strategists, and social media specialists. General applications are always welcome.",
      },
      { property: "og:url", content: "https://mgcontentlabs.com/careers" },
      { property: "og:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
      { name: "twitter:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
    ],
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/careers" }],
  }),
  component: CareersPage,
});

const benefits = [
  {
    title: "Fully Remote",
    description: "Work from anywhere in India with async-friendly collaboration and clear handoffs.",
  },
  {
    title: "Editorial Rigor",
    description: "Learn from senior strategists who care about craft, structure, and evidence over fluff.",
  },
  {
    title: "Real Portfolio Growth",
    description: "Build case-worthy work for B2B and SaaS brands — the kind of clips that advance your career.",
  },
  {
    title: "Flexible Schedule",
    description: "We measure output and deadlines, not hours logged. Take ownership of your calendar.",
  },
];

function CareersPage() {
  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />

      {/* Hero */}
      <header className="bg-brand-muted py-24 lg:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Careers</span>
          <h1 className="mt-6 font-serif text-5xl leading-[1.05] md:text-7xl">
            Build content that <span className="italic text-brand-accent">gets cited.</span>
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-brand-deep/70">
            Join a remote-first digital marketing team in India working with B2B and SaaS brands around the world.
            We're always looking for writers, strategists, and distribution specialists who think in systems.
          </p>
        </div>
      </header>

      {/* Why join */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Why MG Content Labs</span>
            <h2 className="mt-4 font-serif text-4xl">A team built around ideas that compound</h2>
            <p className="mx-auto mt-4 max-w-2xl text-brand-deep/60">
              We hire people who want to do their best work — then give them the focus, feedback, and freedom to do it.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b) => (
              <div key={b.title} className="border border-brand-deep/10 bg-brand-cream p-8">
                <h3 className="font-serif text-xl">{b.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-brand-deep/70">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* No open roles */}
      <section className="bg-brand-deep py-24 text-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Open Roles</span>
          <h2 className="mt-4 font-serif text-4xl">No open roles right now</h2>
          <p className="mx-auto mt-6 max-w-2xl leading-relaxed text-white/70">
            All current positions have been filled. We still keep a rolling talent pool — if you're a writer, SEO
            strategist, or social media specialist who thinks in systems, send us your resume and we'll reach out when
            something opens up.
          </p>
          <div className="mt-10 flex justify-center">
            <a
              href="mailto:mgcontentservices@gmail.com?subject=General application"
              className={button3DClass("accent")}
            >
              Join the Talent Pool
            </a>
          </div>
        </div>
      </section>

      {/* Hiring process */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">How We Hire</span>
            <h2 className="mt-4 font-serif text-4xl">Simple, respectful, and fast</h2>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              { step: "01", title: "Apply", description: "Send your resume, portfolio, and a short note on why this role fits you." },
              { step: "02", title: "Screening", description: "A 20-minute conversation to understand your experience, motivations, and working style." },
              { step: "03", title: "Task", description: "A brief, paid writing or strategy task so we can see your thinking in context." },
              { step: "04", title: "Offer", description: "Final conversation, references, and a fair offer with clear growth expectations." },
            ].map((p) => (
              <div key={p.step} className="border-t border-brand-accent pt-8">
                <div className="font-sans text-3xl font-bold text-brand-accent">{p.step}</div>
                <h3 className="mt-4 font-serif text-xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-deep/70">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-muted py-24 text-center">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-serif text-4xl md:text-5xl">
            Don't see the right role? <span className="italic text-brand-accent">Say hello anyway.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-brand-deep/70">
            We're always open to meeting writers, strategists, SEOs, and social specialists who care about compounding content.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href="mailto:mgcontentservices@gmail.com?subject=General application" className={button3DClass("primary")}>
              Send Your Resume
            </a>
            <Link to="/contact" className={button3DClass("secondary")}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
